<script setup>
import { ref, nextTick } from 'vue';

/* Einheitliches Muster zum Anlegen neuer Einträge: erst nur ein Knopf,
 * das Formular erscheint auf Klick.
 *
 * Warum nicht das Formular dauerhaft zeigen: In den Reitern stehen mehrere
 * Gruppen untereinander. Wären überall Eingabefelder offen, sähe die Ansicht
 * nach Formular aus statt nach Akte — obwohl man meistens nur nachschaut.
 *
 * Ausnahme sind die festen Dokumentenplätze: Dort gibt es je Spieler genau
 * einen Eintrag, der entweder da ist oder nicht. Ein Aufklappen wäre dort
 * ein Klick ohne Gewinn.
 */

const props = defineProps({
  label: { type: String, required: true },
  /* Beschriftung des Bestätigungsknopfes; Vorgabe ist "Hinzufügen". */
  submitLabel: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  /* Solange true, lässt sich nicht bestätigen — für Pflichtfelder. */
  invalid: { type: Boolean, default: false },
  busy: { type: Boolean, default: false },
});

const emit = defineEmits(['submit', 'cancel', 'open']);

const open = ref(false);
const body = ref(null);

async function start() {
  open.value = true;
  emit('open');
  /* Auf das erste Eingabefeld springen — sonst muss man nach dem Klick
     noch einmal klicken, um tippen zu können. */
  await nextTick();
  /* Ohne preventScroll springt der Browser nur zum Eingabefeld, und Abbrechen
     und Hinzufügen bleiben unter der Speichern-Leiste des Panels. */
  body.value?.querySelector('input, select, textarea')?.focus({ preventScroll: true });
  body.value?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
}

function cancel() {
  open.value = false;
  emit('cancel');
}

function submit() {
  if (props.invalid || props.busy) return;
  emit('submit', { close: () => (open.value = false) });
}

defineExpose({ close: () => (open.value = false) });
</script>

<template>
  <div class="add-panel">
    <button v-if="!open" class="btn ghost small" :disabled="disabled" @click="start">
      <span class="ap-plus" aria-hidden="true">+</span> {{ label }}
    </button>

    <div v-else ref="body" class="add-panel-form">
      <div class="apf-title">{{ label }}</div>

      <slot />

      <div class="apf-actions">
        <button class="icon-btn" @click="cancel">{{ $t('common.cancel') }}</button>
        <button class="btn small" :disabled="invalid || busy" @click="submit">
          {{ busy ? $t('common.loading') : submitLabel || $t('common.add') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.add-panel {
  margin-top: 10px;
}
.add-panel-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  background: var(--card);
  border: 1px solid var(--green-dim);
  border-radius: 10px;
}
.apf-title {
  font-size: 0.7rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--green);
}
/* Klebt am unteren Rand des Scrollbereichs: Auch wenn das Formular höher wird
   (etwa durch Felder, die je nach Auswahl erscheinen) oder auf dem Handy nicht
   ganz auf den Bildschirm passt, bleiben Abbrechen und Hinzufügen erreichbar. */
.apf-actions {
  position: sticky;
  bottom: 0;
  z-index: 1;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;
  margin: 0 -16px -16px;
  padding: 12px 16px 16px;
  background: var(--card);
  border-radius: 0 0 10px 10px;
}
.btn:disabled {
  opacity: 0.5;
  cursor: default;
}
</style>
